import React, { useCallback } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import CommentIcon from '@atlaskit/icon/core/comment';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
	usePanel,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useContentType } from '@confluence/page-context';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import {
	useShowCommentsPanel,
	preloadCommentsPanel,
} from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { UnreadIndicator } from '@confluence/unread-comments/entry-points/UnreadIndicator.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER,
	UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';

const commentIconContainerStyle = css({
	position: 'relative',
});

const iconButtonWrapperStyle = css({
	position: 'relative',
	display: 'inline-flex',
});

const unreadCountBadgeStyle = css({
	position: 'absolute',
	top: '0px',
	right: '0px',
	minWidth: '14px',
	height: '14px',
	borderRadius: '7px',
	backgroundColor: token('color.background.information.bold'),
	color: token('color.text.inverse'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- badge intentionally uses small custom size outside ADS typography scale
	fontSize: '9px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- badge intentionally uses small custom size outside ADS typography scale
	fontWeight: 'bold',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: `0 ${token('space.025')}`,
	pointerEvents: 'none',
	boxSizing: 'border-box',
	zIndex: 1,
});

const i18n = defineMessages({
	viewAllCommentsLabel: {
		id: 'object-sidebar-components.view.all.comments',
		defaultMessage: 'View all comments',
		description: 'Label for button to view all comments by opening the comments panel',
	},
	showCommentsLabel: {
		id: 'object-sidebar-components.show.comments.label',
		description:
			"The text is shown in a tooltip when the Object Sidebar's Comments button is hovered.",
		defaultMessage: 'Show comments',
	},
	showReactionsLabel: {
		id: 'object-sidebar-components.show.reactions.label',
		description:
			"The text is shown in a tooltip when the Object Sidebar's Comments button is hovered while displaying an inline reaction.",
		defaultMessage: 'Show reactions',
	},
});

export type CommentPanelButtonProps = {
	onClick?: () => void;
	contentId: string;
	collapsed?: boolean;
	testId?: string;
	isStandaloneComment?: boolean;
	isDisabled?: boolean;
	isViewAllIcon?: boolean;
	isReaction?: boolean;
};

export const CommentPanelButton = ({
	onClick,
	collapsed,
	testId,
	isStandaloneComment = false,
	isDisabled = false,
	isViewAllIcon = false,
	isReaction = false,
}: CommentPanelButtonProps) => {
	const pageMode = useGetPageMode();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();
	const {
		viewProps,
		selectionProps,
		hoverProps,
		editorViewProps,
		editorCreateProps,
		showCreateComponent,
		selectionOptions,
	} = useAnnotations();
	const {
		setViewProps,
		setSelectionProps,
		setHoverProps,
		setEditorViewProps,
		setEditorCreateProps,
		setShowCreateComponent,
		setSelectionOptions,
	} = useAnnotationsDispatch();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const panel = usePanel();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();
	const { newCommentHighlight, activeHighlight } = useInlineCommentsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const { formatMessage } = useIntl();
	const [{ isViewMode: isLiveViewMode, isEditMode: isLiveEditMode }] = useLivePageMode();
	const dataTestId = testId || 'object-sidebar-comments-button-id';
	const [contentTypeFromState] = useContentType();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const allowComments = useEmbeddedComments();

	const { unreadCommentIDSet } = useUnreadCommentsState();
	const showUnreadCountBadge = !isStandaloneComment && (unreadCommentIDSet?.size ?? 0) > 0;

	const commentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
		isEditorShortcut: pageMode === 'edit' || isLiveEditMode,
	});

	// keyboard events do not bubble up to confluence when focus is in the db iframe.
	// Since the keyboard shortcut does not work, we must hide the shortcut text.
	const isShortcutTextShown = contentTypeFromState !== ContentType.DATABASE;

	// Preload components on hover to improve FMP performance
	const handleMouseEnter = useCallback(() => {
		preloadCommentsPanel(pageMode);
	}, [pageMode]);

	const handleCommentClick = useCallback(() => {
		// FABRIC PAGES
		// Close the view component if it's open
		if (viewProps) {
			// Inform the Renderer to de-select the annotation
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			// Remove the display of the comment
			setViewProps(null);
		}
		if (showCreateComponent && selectionOptions) {
			// Inform CreateComment to reset the state
			setShowCreateComponent(false);
			setSelectionOptions(undefined);
		}

		if (editorViewProps) {
			// Inform the editor to de-select the annotation
			eventEmitter.emit('setselectedannotation', '');
			// Remove the display of the comment
			setEditorViewProps(null);
		}

		if (selectionProps) {
			selectionProps.onClose();
			setSelectionProps(null);
		}

		if (hoverProps) {
			hoverProps.onClose();
			setHoverProps(null);
		}

		if (editorCreateProps) {
			editorCreateProps?.onClose && editorCreateProps.onClose();
			setEditorCreateProps(null);
		}

		// LEGACY PAGES
		if (newCommentHighlight || activeHighlight) {
			// Close the legacy inline comment panel
			window.dispatchEvent(new Event('closeLegacyInlineCommentSidebar'));
		}

		if (panel?.id === 'commentsPanel') {
			void hideObjectSidebar();
		} else {
			void showCommentsPanel();
		}
	}, [
		eventEmitter,
		viewProps,
		editorViewProps,
		selectionProps,
		hoverProps,
		newCommentHighlight,
		activeHighlight,
		setViewProps,
		setEditorViewProps,
		setSelectionProps,
		setHoverProps,
		showCommentsPanel,
		hideObjectSidebar,
		panel,
		editorCreateProps,
		setEditorCreateProps,
		showCreateComponent,
		selectionOptions,
		setShowCreateComponent,
		setSelectionOptions,
	]);

	const checkUnsavedOnClickCommentsButton = useCallback(
		({
			triggeredByKeyboardShortcut = false,
			isViewAllIcon = false,
		}: {
			triggeredByKeyboardShortcut?: boolean;
			isViewAllIcon?: boolean;
		} = {}) =>
			() => {
				// Early return for ViewAll icon - no action needed
				if (isViewAllIcon) {
					onClick?.();
					return;
				}

				if (hasContentChanged) {
					showModal(CommentWarningDialog, {
						onConfirm: () => {
							resetContentChanged();
							handleCommentClick();
							resetInitialDataLoadedMap();
						},
					});
				} else {
					handleCommentClick();
				}

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'commentsPanel',
						source: 'contentWrapper',
						attributes: {
							triggeredByKeyboardShortcut,
							contentType: contentTypeFromState,
							pageMode,
						},
					},
				}).fire();
			},
		[
			onClick,
			hasContentChanged,
			handleCommentClick,
			resetContentChanged,
			resetInitialDataLoadedMap,
			showModal,
			createAnalyticsEvent,
			contentTypeFromState,
			pageMode,
		],
	);

	const shortcutListeners = (
		<>
			{(pageMode === 'view' || isLiveViewMode) && (
				<GeneralShortcutListener
					deprecatedAccelerator={TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER}
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={checkUnsavedOnClickCommentsButton({ triggeredByKeyboardShortcut: true })}
				/>
			)}
			{(pageMode === 'edit' || isLiveEditMode) && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={checkUnsavedOnClickCommentsButton({ triggeredByKeyboardShortcut: true })}
				/>
			)}
		</>
	);

	if (collapsed) {
		return shortcutListeners;
	}

	return (
		<div
			css={commentIconContainerStyle}
			data-testid={dataTestId}
			data-vc="object-sidebar-comments-button"
		>
			<Tooltip
				position="left"
				content={
					<FormattedMessage
						{...(isReaction && expVal('confluence_inline_reactions', 'isEnabled', false)
							? i18n.showReactionsLabel
							: i18n.showCommentsLabel)}
					/>
				}
				shortcut={isShortcutTextShown ? commentShortcutStrings : undefined}
				delay={0}
				hideTooltipOnClick
			>
				{allowComments && (
					<>
						{expVal('cc_spark_show_unread_count_in_comments_button', 'isEnabled', false) ? (
							<span css={iconButtonWrapperStyle}>
								<IconButton
									onClick={checkUnsavedOnClickCommentsButton({ isViewAllIcon })}
									onMouseEnter={handleMouseEnter}
									appearance="subtle"
									spacing={isStandaloneComment ? 'compact' : undefined} // Needed to get the correct overall size to match the X button
									shape={isRovoButtonEnabled ? 'default' : 'circle'}
									icon={
										isStandaloneComment &&
										expValEquals('confluence_comments_cohiti_panel_icon_update', 'isEnabled', true)
											? PanelRightIcon
											: CommentIcon
									}
									label={formatMessage(i18n.viewAllCommentsLabel)}
									testId="sidebar-comments-button"
									interactionName="view-comments-menu-action"
									isDisabled={
										isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
									}
								/>
								{showUnreadCountBadge && (
									<span css={unreadCountBadgeStyle} data-testid="comment-button-unread-count-badge">
										{unreadCommentIDSet.size > 9 ? '9+' : String(unreadCommentIDSet.size)}
									</span>
								)}
							</span>
						) : (
							<>
								<IconButton
									onClick={checkUnsavedOnClickCommentsButton({ isViewAllIcon })}
									onMouseEnter={handleMouseEnter}
									appearance="subtle"
									spacing={isStandaloneComment ? 'compact' : undefined} // Needed to get the correct overall size to match the X button
									shape={isRovoButtonEnabled ? 'default' : 'circle'}
									icon={
										isStandaloneComment &&
										expValEquals('confluence_comments_cohiti_panel_icon_update', 'isEnabled', true)
											? PanelRightIcon
											: CommentIcon
									}
									label={formatMessage(i18n.viewAllCommentsLabel)}
									testId="sidebar-comments-button"
									interactionName="view-comments-menu-action"
									isDisabled={
										isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
									}
								/>
								{!isStandaloneComment && unreadCommentIDSet?.size > 0 && <UnreadIndicator />}
							</>
						)}
					</>
				)}
				{!isStandaloneComment && shortcutListeners}
			</Tooltip>
		</div>
	);
};
